// Live state from Foundry, where the campaign is played. The Foundry module (ktb-lancer-foundry) saves it to /api/state
// (functions/api/state.js), and when the site runs inside a Foundry website scene it also sends it straight here.
// Whatever Foundry has overrides the site's files; with nothing from Foundry (local dev, a section never linked) the
// files stand.
//
// - pilots: each pilot's condition (bond stress, XP, burdens), build (level, HASE, skills, talents, licenses, gear) and
//   active mech (name, frame, loadout, HP, structure, stress, heat...), keyed by callsign slug;
// - clocks: Global Progress Clocks the GM chose to show on the site, added to the Status page's clocks;
// - relationships: NPC relationship clocks linked in Foundry, { npcId: { value, mood } };
// - factions: the Westwind Company's standing with factions linked in Foundry, { factionId: -4..+4 };
// - reserves: the company reserves in Foundry, which replace the reserve files once Foundry keeps them;
// - engagements, totals, history: fights Foundry filed when they ended, for the Logs page.
import { reactive } from "vue";
import { mechFromFoundry } from "@/data/pilots.js";

export const live = reactive({
	pilots: {},
	clocks: [],
	relationships: {},
	factions: {},
	// null until Foundry keeps the reserves
	reserves: null,
	engagements: [],
	totals: {},
	history: [],
	// "foundry" when sent by the Foundry page around this one, "saved" from /api/state, "" when nothing came
	source: "",
});

/** "Second Wind" → "second-wind", as the Foundry module keys pilots. */
export function pilotKey(callsign) {
	return String(callsign ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");
}

/** True when the site is shown inside another page (a Foundry website scene). */
export const embedded = typeof window !== "undefined" && window.parent !== window;

const isObject = value => value && typeof value === "object" && !Array.isArray(value);

/** Takes the sections a message or the API carries; the ones missing stay as they are. */
function apply(state, source) {
	if (isObject(state.pilots)) live.pilots = state.pilots;
	if (Array.isArray(state.clocks)) live.clocks = state.clocks;
	if (isObject(state.relationships)) live.relationships = state.relationships;
	if (isObject(state.factions)) live.factions = state.factions;
	if (Array.isArray(state.reserves)) live.reserves = state.reserves;
	live.source = source;
}

export function startLiveState() {
	fetch("/api/state", { cache: "no-store" })
		.then(response => (response.ok ? response.json() : null))
		.then(state => {
			if (!state) return;
			// Foundry's own values, if they already came, are newer than the saved ones
			if (live.source !== "foundry") apply(state, "saved");
			if (Array.isArray(state.engagements)) live.engagements = state.engagements;
			if (isObject(state.totals)) live.totals = state.totals;
			if (Array.isArray(state.history)) live.history = state.history;
		})
		.catch(() => {});

	if (!embedded) return;
	window.addEventListener("message", event => {
		if (event.source !== window.parent) return;
		if (event.data?.type === "ktb:state" && isObject(event.data)) apply(event.data, "foundry");
	});
	// Tell the Foundry page this one is ready for data
	window.parent.postMessage({ type: "ktb:ready" }, "*");
}

/** Inside Foundry: asks it to open something's sheet ("pilot" or "mech", by callsign). */
export function openInFoundry(kind, callsign) {
	if (embedded) window.parent.postMessage({ type: "ktb:open", kind, key: pilotKey(callsign) }, "*");
}

const count = value => Math.max(0, Math.round(Number(value) || 0));
const ROMAN = ["", "I", "II", "III"];

/** A pilot's build as the pilot files write it: "Hack or Fix +2", "Ace II", "IPS-N Blackbeard II". */
function liveBuild(build) {
	return {
		level: count(build.level),
		hase: Array.isArray(build.hase) ? build.hase.slice(0, 4).map(count) : undefined,
		skills: (build.skills || []).map(skill => ({
			name: `${skill.name} +${count(skill.rank) * 2}`,
			description: "",
		})),
		talents: (build.talents || []).map(talent =>
			`${talent.name} ${ROMAN[count(talent.rank)] || ""}`.trim()
		),
		licenses: (build.licenses || []).map(license =>
			[license.manufacturer, license.name, ROMAN[count(license.rank)]].filter(Boolean).join(" ")
		),
		gear: {
			armor: build.gear?.armor || [],
			weapons: build.gear?.weapons || [],
			items: build.gear?.items || [],
		},
	};
}

/** The pilot with what Foundry has: condition, build and active mech. The pilot as it is otherwise. */
export function withLivePilot(pilot) {
	const state = live.pilots[pilotKey(pilot.callsign)];
	if (!state) return pilot;
	const condition = {
		stress: count(state.stress),
		stressMax: count(state.stressMax) || pilot.condition.stressMax,
		xp: count(state.xp),
		burdens: (Array.isArray(state.burdens) ? state.burdens : []).map(burden => ({
			name: String(burden?.name ?? ""),
			progress: count(burden?.progress),
			segments: count(burden?.segments) || null,
		})),
	};
	const build = isObject(state.build) ? liveBuild(state.build) : {};
	if (!build.hase) delete build.hase;
	return {
		...pilot,
		...build,
		condition: { ...pilot.condition, ...condition },
		bond: pilot.bond ? { ...pilot.bond, ...condition } : pilot.bond,
		mech: isObject(state.mech)
			? mechFromFoundry(state.mech, pilot.callsign, pilot.mech)
			: pilot.mech,
	};
}

/** The NPC with Foundry's relationship clock and mood, when it's linked there. */
export function withLiveNpc(npc) {
	const state = live.relationships[npc.id];
	if (!state) return npc;
	return {
		...npc,
		clockValue: Math.max(1, Math.min(10, Math.round(Number(state.value) || npc.clockValue))),
		clockMood: state.mood || npc.clockMood,
	};
}

/** The factions, with the Westwind Company's standings that Foundry keeps laid over its file's. */
export function withLiveFactions(factions, partyId) {
	if (!Object.keys(live.factions).length) return factions;
	return factions.map(faction =>
		faction.id === partyId
			? { ...faction, relations: { ...faction.relations, ...live.factions } }
			: faction
	);
}

/** The Status page's clocks: the files' and the pilots', then the ones the GM shows from Foundry. */
export function withLiveClocks(clocks) {
	return [...clocks, ...live.clocks.map(clock => ({ result: "", ...clock }))];
}

/** The reserves: Foundry's once it keeps them, the files' until then. */
export function withLiveReserves(reserves) {
	return Array.isArray(live.reserves) ? live.reserves : reserves;
}
